'use client';
import React from 'react';
import AppImage from '@/components/ui/AppImage';
import { Lock, Globe, MapPin } from 'lucide-react';
import type { Board } from './SimpanClient';

interface Props {
  boards: Board[];
  onSelectBoard: (board: Board) => void;
}

export default function BoardsGrid({ boards, onSelectBoard }: Props) {
  if (boards.length === 0) {
    return (
      <div className="text-center py-20">
        <div className="text-5xl mb-4">📋</div>
        <p className="text-base font-700 text-primary mb-2">Belum ada board</p>
        <p className="text-sm text-muted-foreground">Buat board pertamamu untuk mulai mengumpulkan tempat favorit!</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-5">
      {boards.map((board) => (
        <button
          key={`board-${board.id}`}
          onClick={() => onSelectBoard(board)}
          className="place-card text-left w-full"
        >
          {/* Cover Photo Grid */}
          <div className="relative h-44 bg-muted overflow-hidden">
            {board.coverPhotos.length === 0 ? (
              <div className="w-full h-full flex items-center justify-center bg-secondary">
                <span className="text-4xl">📋</span>
              </div>
            ) : board.coverPhotos.length === 1 ? (
              <AppImage
                src={board.coverPhotos[0]}
                alt={`Cover board ${board.title}`}
                fill
                className="object-cover"
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
              />
            ) : board.coverPhotos.length === 2 ? (
              <div className="grid grid-cols-2 h-full">
                {board.coverPhotos.slice(0, 2).map((photo, i) => (
                  <div key={`cover-${board.id}-${i}`} className="relative overflow-hidden">
                    <AppImage
                      src={photo}
                      alt={`Cover ${board.title} foto ${i + 1}`}
                      fill
                      className="object-cover"
                      sizes="12.5vw"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 grid-rows-2 h-full gap-0.5">
                {board.coverPhotos.slice(0, 4).map((photo, i) => (
                  <div key={`cover-${board.id}-${i}`} className="relative overflow-hidden">
                    <AppImage
                      src={photo}
                      alt={`Cover ${board.title} foto ${i + 1}`}
                      fill
                      className="object-cover"
                      sizes="12.5vw"
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Privacy Badge */}
            <div className="absolute top-3 right-3">
              <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-600 ${board.isPublic ? 'bg-card/90 text-primary' : 'bg-card/90 text-muted-foreground'}`}>
                {board.isPublic ? <Globe size={11} /> : <Lock size={11} />}
                {board.isPublic ? 'Publik' : 'Privat'}
              </span>
            </div>
          </div>

          {/* Info */}
          <div className="p-4">
            <h3 className="text-sm font-800 text-foreground mb-0.5">{board.title}</h3>
            {board.description && (
              <p className="text-xs text-muted-foreground line-clamp-1 mb-2">{board.description}</p>
            )}
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin size={11} className="text-accent" />
              <span className="font-600 text-foreground font-tabular">{board.placeCount}</span>
              <span>tempat</span>
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}